import React, { useEffect } from 'react';
import get from 'lodash.get';
import { FlexBox, Grid, MuiBox, Typography } from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { useStyles } from '../styled';
import { useTranslation } from 'react-i18next';
import { ComponentProps } from '../types';
import { ActionOverlay } from 'components/ActionOverlay';
import { selectors as cartSelectors } from 'services/cart';
import { selectors as configSelectors, Configuration, Messages, TicketDeliverySettings } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import EmailTicket from 'assets/EmailTicket.png';

export const EmailDelivery = (props: ComponentProps) => {
  const { onCompletion, onErrorConfirmation } = props;
  const { t, i18n } = useTranslation();

  // Action Creators and Selectors
  const checkoutCart = useSelector(cartSelectors.selectCheckoutCart);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const deliveryStatus = useSelector(ticketingSelectors.selectDeliveryStatus);
  const ticketingError = useSelector(ticketingSelectors.selectError);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const support = get(config, `${ticketingPath}.supportName.${i18n.language}`, t('commonText.support'));
  const emailDeliveryConfig: TicketDeliverySettings['email'] = get(
    config,
    `${ticketingPath}.ticketDeliverySettings.email`,
    {}
  );
  const deliveryMessages: Messages = get(emailDeliveryConfig, `deliveryInProgressMessages`, {});
  const deliveryHeaderText: string = get(
    deliveryMessages,
    `header.${i18n.language}`,
    `${t('delivery.sendingYour')} ${t('delivery.tickets')}`
  );
  const deliverySubheaderText: string = get(
    deliveryMessages,
    `subheader.${i18n.language}`,
    t('delivery.checkInboxForTicket')
  );
  const errorOverlayMessages: Messages = get(emailDeliveryConfig, `errorOverlayMessages`, {});
  const errorOverlayHeaderText: string = get(
    errorOverlayMessages,
    `header.${i18n.language}`,
    t('delivery.deliveryError')
  );
  const errorOverlaySubheaderText: string = get(errorOverlayMessages, `subheader.${i18n.language}`, '');
  const errorOverlayConfirmText: string = get(errorOverlayMessages, `buttons.${i18n.language}[0]`, t('commonText.ok'));

  const classes = useStyles();

  useEffect(() => {
    // On completion
    if (deliveryStatus === 'completed') {
      onCompletion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStatus]);

  /**
   * Returns text to use as the subheader for the error overlay.
   * The configuration allows for one macro: {{orderId}}. If not configured,
   * there are two default strings depending on if the orderId is known.
   */
  const getErrorOverlaySubheader = (): string => {
    let errorMessage: string;
    const orderId = (checkoutCart && checkoutCart.orderId) || '';

    if (errorOverlaySubheaderText) {
      errorMessage = errorOverlaySubheaderText.replace('{{orderId}}', orderId);
    } else if (orderId) {
      errorMessage = t('saleError.printErrorMsg', { orderId: `#${orderId}`, support });
    } else {
      errorMessage = t('saleError.notifySupport', { support });
    }

    return errorMessage;
  };

  return (
    <>
      <Grid className={classes.deliveryOuterContainer}>
        <Grid container className={classes.deliveryInnerContainer}>
          <Grid item lg={12} xl={6} className={classes.deliveryTextContainer}>
            {/* Until email api call, show email content - otherwise, re-route to CompletionView */}
            <MuiBox className={classes.deliveryPrintingText}>
              <Grid className={classes.deliveryPrintingHeader}>
                <Typography
                  fontSize={100}
                  fontWeight={600}
                  sx={{ lineHeight: 1.1, whiteSpace: 'pre-wrap' }}
                  align="center"
                >
                  {deliveryHeaderText}
                </Typography>
              </Grid>
              <Typography fontSize={30}>{deliverySubheaderText}</Typography>
            </MuiBox>
          </Grid>
          <Grid item lg={12} xl={6} className={classes.deliveryImageContainer}>
            <FlexBox fullHeight padding="0 20" align="center" justify="center">
              <img src={EmailTicket} alt="email-ticket" width="100%" />
            </FlexBox>
          </Grid>
        </Grid>
      </Grid>

      {ticketingError && (
        <ActionOverlay
          title={errorOverlayHeaderText}
          description={
            <Typography align="center" fontSize={30}>
              {getErrorOverlaySubheader()}
            </Typography>
          }
          confirmText={errorOverlayConfirmText}
          onConfirm={onErrorConfirmation}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}
    </>
  );
};
